import {
  getSelectedPatternStyle,
  getSelectedStrokeStyle,
} from 'components/Map/utils';
import { Layer, LayerProps } from 'shared/map-exports';
import { useMapDrawerEntityForPoiState } from 'state/useMapDrawerEntityForPoiState';
import { useMapLayerEvents } from '../useMapLayerEvents';
import {
  ExternalFeatureLayerProps,
  ExternalFeatureProperties,
} from './ExternalFeatureLayer.types';
import { useLayerClickHandler } from './useLayerClickHandler';
import { EXTERNAL_LAYERS_BEFORE_ID } from './ExternalFeatureLayer.constants';

type PolygonLayersProps = ExternalFeatureLayerProps;

/**
 * Renders whatever layers are defined in the `displayStyleData` of the feature layer, and:
 * - Adds a selected fill layer and a selected stroke layer that highlight the currently selected polygon.
 * - Uses the `clickListenerLayerId` to handle click events on the polygons.
 */
export const EmbeddedStylePolygonLayer = (
  props: PolygonLayersProps,
): JSX.Element => {
  const {
    sourceId,
    featureLayer: { shortName, displayStyleData, displayName, geomType },
  } = props;
  const { mapDrawerEntityForPoi } = useMapDrawerEntityForPoiState();
  const clickHandler = useLayerClickHandler(sourceId, displayName, geomType);

  if (displayStyleData.displayStyleType !== 'embedded') {
    throw new Error(
      'EmbeddedStyle*Layer requires displayStyleType to be embedded',
    );
  }

  useMapLayerEvents<ExternalFeatureProperties>({
    layerId: displayStyleData.clickListenerLayerId,
    onClick: clickHandler,
  });

  const selectedId =
    (mapDrawerEntityForPoi?.type === sourceId && mapDrawerEntityForPoi?.id) ||
    '';

  const selectedFillStyle = getSelectedPatternStyle({
    idKey: ['id'],
    otherLayerProps: {
      source: sourceId,
      'source-layer': shortName,
    },
    selectedId,
  });

  const selectedStrokeStyle = getSelectedStrokeStyle({
    idKey: ['id'],
    otherLayerProps: {
      source: sourceId,
      'source-layer': shortName,
      paint: {
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 18, 6],
      },
    },
    selectedId,
  });

  return (
    <>
      {displayStyleData.layers
        // Add the `source` property to each layer's style so it references the correct
        // <Source>.
        .map((layer) => ({ ...layer, source: sourceId }) as LayerProps)
        .map((layer) => (
          <Layer
            key={layer.id}
            {...layer}
            beforeId={EXTERNAL_LAYERS_BEFORE_ID}
          />
        ))}

      <Layer {...selectedFillStyle} beforeId={EXTERNAL_LAYERS_BEFORE_ID} />
      <Layer {...selectedStrokeStyle} beforeId={EXTERNAL_LAYERS_BEFORE_ID} />
    </>
  );
};
